import { useTranslation } from 'react-i18next';
import { Search, ChevronRight, BookOpen, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { InteractiveCard } from '@/components/ui/interactive-card';
import type { KnowledgeCategory, KnowledgeSearchResult } from '@/types/knowledgeBook';

type Props = {
  categories: KnowledgeCategory[];
  loading: boolean;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  searchResults: KnowledgeSearchResult[];
  onSelectCategory: (slug: string) => void;
  onSelectArticle: (catSlug: string, artSlug: string) => void;
};

export default function CategoriesView({
  categories,
  loading,
  searchQuery,
  onSearchChange,
  searchResults,
  onSelectCategory,
  onSelectArticle,
}: Props) {
  const { t } = useTranslation();
  return (
    <>
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <BookOpen className="size-8 text-primary" />
          <h1 className="text-3xl font-heading text-foreground">{t('knowledge.title')}</h1>
        </div>
        <p className="text-muted-foreground">{t('knowledge.subtitle')}</p>
      </div>

      {/* Search */}
      <div className="relative mb-8">
        <Search className="absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t('knowledge.searchPlaceholder')}
          className="rounded-xl py-3 pl-10"
        />
      </div>

      {/* Search results */}
      {searchQuery.length >= 2 ? (
        <div>
          <p className="mb-4 text-sm text-muted-foreground">
            {t('knowledge.resultsCount', { count: searchResults.length, query: searchQuery })}
          </p>
          {searchResults.length > 0 ? (
            <div className="space-y-2">
              {searchResults.map((result) => (
                <InteractiveCard
                  key={result.id}
                  onClick={() => onSelectArticle(result.categorySlug, result.slug)}
                  className="items-start rounded-xl p-4"
                >
                  <span className="mt-0.5 text-lg">{result.categoryIcon}</span>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-foreground">{result.title}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{result.categoryTitle}</p>
                    <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{result.preview}</p>
                  </div>
                  <ChevronRight className="mt-1 size-4 shrink-0 text-muted-foreground" />
                </InteractiveCard>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground/60">{t('knowledge.noResults')}</p>
          )}
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="size-6 animate-spin text-primary" />
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {categories.map((cat) => (
            <InteractiveCard
              key={cat.id}
              onClick={() => onSelectCategory(cat.slug)}
              className="flex-col items-start rounded-xl p-5"
            >
              <div
                className="mb-3 flex size-11 items-center justify-center rounded-xl text-xl"
                style={{ backgroundColor: `${cat.color}15` }}
              >
                {cat.icon}
              </div>
              <h3 className="font-semibold text-foreground">{cat.title}</h3>
              <p className="mt-1.5 flex-1 text-sm text-muted-foreground leading-relaxed">{cat.description}</p>
              <div className="mt-3 flex w-full items-center justify-between">
                <span className="text-xs text-muted-foreground/60">
                  {t('knowledge.articleCount', { count: cat.articleCount })}
                </span>
                <ChevronRight className="size-4 text-muted-foreground" />
              </div>
            </InteractiveCard>
          ))}
        </div>
      )}
    </>
  );
}
